import React, { useCallback, useEffect, useRef, useState } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSend, faTimes } from '@fortawesome/pro-solid-svg-icons'
import { useSelector } from 'react-redux'
import { Input } from 'antd'
import { debounce, isArray, isFunction } from 'lodash'

import { Keyboard } from '../../../utils/constants'
import { onError, onInfo, onSuccess, resolvePromises } from '../../../utils/apiHelper'
import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import { selectUser } from '../../../reducers/UserReducer'
import { getChatRoomMessages, postMessage } from '../../../utils/api/chatRoom'
import IconButton from '../../antd/Buttons/IconButton'
import Loading from '../Loading'
import ChatMessage from './ChatMessage'
import { getFullNameOrEmail } from '../../../utils/entities/user'
import { HttpStatuses } from '../../../utils/http'
import ButtonUploader from '../../antd/Uploaders/ButtonUploader'
import FilePreview from '../FilePreview'
import { uploadMessageFile } from '../../../utils/api/chatMessage'

import '../../../assets/chat-widget.scss'

const { TextArea } = Input

export const MESSAGE_RECEIVED = 'MESSAGE_RECEIVED'
export const MESSAGE_SENT = 'MESSAGE_SENT'
const DEFAULT_CONTAINER_STYLE = { height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }
const SYNC_TIME = 5000

const ChatRooms = ({ loading: externalLoading, chatRooms, title, onClose, onChatRoomCreate }) => {
  const t = useSelector(selectTranslate)
  const user = useSelector(selectUser)

  const [loading, setLoading] = useState(true)
  const [message, setMessage] = useState('')
  const [messageFiles, setMessageFiles] = useState([])
  const [activeChatRoom, setActiveChatRoom] = useState(null)
  const [messages, setMessages] = useState([])
  const [queuedMessages, setQueuedMessages] = useState([])

  const messagesKeepAlive = useRef(null)
  const messagesContainer = useRef(null)
  const mounted = useRef(true)

  const safeSetState = useCallback((setter, data) => {
    if (mounted.current) {
      setter(data)
    }
  }, [])

  const fetchMessages = useCallback(chatRoom => {
    return getChatRoomMessages(user, chatRoom).then(({ data }) => {
      safeSetState(setMessages, data)
    }).catch(error => {
      console.error('Error while fetching messages:', error)
      messagesKeepAlive.current && clearInterval(messagesKeepAlive.current)
    })
  }, [safeSetState, user.id])

  const scrollToBottom = useCallback(() => {
    messagesContainer.current.scrollTop = messagesContainer.current.scrollHeight
  }, [])

  useEffect(() => {
    mounted.current = true

    return () => {
      messagesKeepAlive.current && clearInterval(messagesKeepAlive.current)
      mounted.current = false
    }
  }, [])

  useEffect(() => {
    if (isArray(chatRooms) && chatRooms.length > 0) {
      setLoading(true)
      setActiveChatRoom(chatRooms[0])
    } else {
      setActiveChatRoom(null)
      safeSetState(setMessages, [])
    }
  }, [chatRooms])

  useEffect(() => {
    messagesKeepAlive.current && clearInterval(messagesKeepAlive.current)

    if (activeChatRoom) {
      safeSetState(setLoading, true)

      fetchMessages(activeChatRoom).finally(() => {
        safeSetState(setLoading, false)
      })
      messagesKeepAlive.current = setInterval(() => fetchMessages(activeChatRoom), SYNC_TIME)
    } else {
      setLoading(false)
      safeSetState(setMessages, [])
    }
  }, [activeChatRoom, fetchMessages, safeSetState])

  useEffect(() => {
    if (queuedMessages.length > 0) {
      safeSetState(setMessage, queuedMessages.join('\n'))
      safeSetState(setQueuedMessages, [])
    }
  }, [queuedMessages, activeChatRoom])

  useEffect(() => {
    const scroll = debounce(() => scrollToBottom(), 20)

    if (messages.length > 0) {
      scroll()
    }

    return () => {
      scroll.cancel()
    }
  }, [messages.length, scrollToBottom])

  const handleKeyUp = e => {
    if (e.key === Keyboard.ENTER && e.ctrlKey && message.trim() !== '') {
      sendMessage()
    }
  }

  const handleChatRoomCreation = () => {
    return onChatRoomCreate(user).then(data => {
      setActiveChatRoom(data)
      onInfo(t('chat_room.creation.success'))

      return data
    }).catch(error => {
      if (error.status === HttpStatuses.CONFLICT && error.type === 'RELOAD_REQUIRED') {
        onInfo(t('chat_room.creation.already_exists'))
      } else {
        onError(error, t('chat_room.creation.error'))
      }

      return Promise.reject(error)
    })
  }

  const sendMessage = () => {
    if (isFunction(onChatRoomCreate) || activeChatRoom) {
      const preCreate = isFunction(onChatRoomCreate) && !activeChatRoom ? handleChatRoomCreation() : Promise.resolve(activeChatRoom)

      preCreate.then(data => {
        if (data === null) {
          return Promise.reject(new Error('No active chat room'))
        } else {
          return postMessage(user, data, { content: message })
        }
      }).then(({ data }) => {
        onSuccess(t('chat_message.send.success'))
        publishFiles(data)
      }).catch(error => {
        if (error.status === HttpStatuses.CONFLICT && error.type === 'RELOAD_REQUIRED') {
          setQueuedMessages(prevQueuedMessages => [...prevQueuedMessages, message])
        } else {
          onError(t('chat_message.send.error'))
          console.error('Error while sending message:', error)
        }
      }).finally(() => {
        setMessage('')
      })
    } else {
      console.error('error.no_active_chat_room')
    }
  }

  const publishFiles = message => {
    if (messageFiles.length > 0) {
      const promisesStack = []

      messageFiles.forEach(file => {
        promisesStack.push(uploadMessageFile(user, message, file))
      })

      resolvePromises(promisesStack).then(responses => {
        if (!isArray(message.documents)) {
          message.documents = []
        }

        responses.forEach(response => {
          if (response?.data) {
            message.documents.push(response.data)
          }
        })

        setMessages(prevMessages => [...prevMessages, message])

        if (message.documents.length === messageFiles.length) {
          onSuccess(t('chat_message.send_files.success'))
        } else if (message.documents.length > 0) {
          onInfo(t('chat_message.send_files.partial_success'))
        } else {
          onError(t('chat_message.send_files.error'))
        }
      }).catch(error => {
        console.error('Error while sending message with files:', error)
        onError(t('chat_message.send_files.error'))
      }).finally(() => {
        setMessageFiles([])
      })
    } else {
      setMessages(prevMessages => [...prevMessages, message])
    }
  }

  return (
    <div className='new-chat-widget flex-column'>
      <div className='top-bar flex-row pd-8'>
        <div className='flex-fill'>{title}</div>
        <FontAwesomeIcon icon={faTimes} className='close-button' onClick={onClose} />
      </div>
      <div
        className='messages-container flex-fill flex-column'
        ref={messagesContainer}
      >
        {(loading || externalLoading) && (
          <div style={DEFAULT_CONTAINER_STYLE}>
            <Loading />
          </div>
        )}
        {!loading && !externalLoading && messages.map(m => (
          <ChatMessage
            key={m.id}
            message={m.content}
            timestamp={m.createdAt}
            username={getFullNameOrEmail(m.createdBy)}
            received={m.createdBy.id !== user.id}
            files={m.documents}
          />
        ))}
        {!loading && !externalLoading && messages.length === 0 && (
          <div style={DEFAULT_CONTAINER_STYLE}>
            {t('common.without_message')}
          </div>
        )}
      </div>
      {messageFiles.length > 0 && (
        <div className='attached-files'>
          {messageFiles.map((file, index) => (
            <div key={index} className='line-top'>
              <FilePreview file={file} onRemove={() => setMessageFiles(prevFiles => prevFiles.filter(f => f.uid !== file.uid))} />
            </div>
          ))}
        </div>
      )}
      <div className='chat-room-actions pd-8'>
        <div className='input-container'>
          <TextArea
            value={message}
            onChange={e => setMessage(e.target.value)}
            placeholder={t('chat_room.message_placeholder')}
            autoSize={{ minRows: 4, maxRows: 4 }}
            onKeyUp={handleKeyUp}
          />
        </div>
        <div className='actions-footer flex-row mt-8'>
          <ButtonUploader type='default' title={t('chat_room.link_a_file')} onUpload={file => setMessageFiles(prevFiles => [...prevFiles, file])} />
          <div className='flex-fill' />
          <IconButton disabled={message.trim() === ''} text={t('common.send')} icon={faSend} size='small' type='primary' onClick={sendMessage} />
        </div>
      </div>
    </div>
  )
}

export default ChatRooms
